import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { Spin, DatePicker } from "antd";
import dayjs from "dayjs";
import { useDashboard } from "../../api/orders/orders";

const { RangePicker } = DatePicker;

// ------------------ Utils ------------
const normalizeCourierName = (title) => {
  if (!title || title.trim() === "") return "Other";
  return title.split(" ")[0].replace(/[^a-zA-Z]/g, "") || "Other";
};

const COLORS = ["#f9a8d4", "#93c5fd", "#c4b5fd", "#86efac", "#67e8f9", "#bef264"];

// ------------------ Component ------------------

const Dashboard = () => {
  const [role, setRole] = useState("user");

  const [dateRange, setDateRange] = useState([
    dayjs().startOf("year"),
    dayjs().endOf("year"),
  ]);

  const { data, isLoading, isError } = useDashboard({
  startDate: dateRange[0]?.format("YYYY-MM-DD"),
  endDate: dateRange[1]?.format("YYYY-MM-DD"),
  // usersid: localStorage.getItem("userId"),
});

  // ✅ Use only localStorage role (no jwt decode)
  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    if (storedRole) {
      setRole(storedRole); // "admin" or "user"
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[400px]">
        <Spin tip="Loading dashboard..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center h-[400px] text-red-500">
        Failed to fetch dashboard data.
      </div>
    );
  }

  const monthlyOrders = data?.monthlyOrders;

  const STATUS_LABELS = {
    shipped: "Shipped",
    notpicked: "Not Picked",
    pickup: "Pickup",
    live: "Live",
    delivered: "Delivered",
    returns: "RTO",
    manifest: "Manifest",
    ndr: "NDR",
  };

  const tabCountsData = Object.keys(STATUS_LABELS).map((key) => ({
    name: STATUS_LABELS[key],
    count: data?.tabCounts?.[key],
  }));

  const codPrepaidData = [
    { name: "COD", value: data?.codOrdersCount },
    { name: "Prepaid", value: data?.prepaidOrdersCount },
  ];

  const groupedCourierCounts = Object.values(
    data?.courierWiseCounts?.reduce((acc, cur) => {
      const main = normalizeCourierName(cur.courier);
      if (!acc[main]) {
        acc[main] = { name: main, value: cur.count };
      } else {
        acc[main].value += cur.count;
      }
      return acc;
    }, {}) || {}
  );

  return (
    <div className="p-6">
      {/* Date Picker */}
      <div className="mb-6">
        <RangePicker
          value={dateRange}
          onChange={(val) => setDateRange(val)}
          format="YYYY-MM-DD"
        />
      </div>

      {/* Top Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white shadow rounded-2xl p-6 flex flex-col items-center">
          <h2 className="text-lg font-semibold text-gray-600">Total Orders</h2>
          <p className="text-3xl font-bold text-blue-600">
            {data?.totalOrdersCount}
          </p>
        </div>

        <div className="bg-white shadow rounded-2xl p-6 flex flex-col items-center">
          <h2 className="text-lg font-semibold text-gray-600">
            Delivered Orders
          </h2>
          <p className="text-3xl font-bold text-green-600">
            {data?.deliveredOrdersCount}
          </p>
        </div>

        {/* ✅ Only show when role === "admin" */}
        {role === "admin" && (
          <div className="bg-white shadow rounded-2xl p-6 flex flex-col items-center">
            <h2 className="text-lg font-semibold text-gray-600">
              Total Customers
            </h2>
            <p className="text-3xl font-bold text-purple-600">
              {data?.totalCustomers}
            </p>
          </div>
        )}
      </div>

      {/* Line Chart */}
      <div className="bg-white shadow rounded-2xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Monthly Orders</h2>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={monthlyOrders}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="orders"
              stroke="#811C50"
              strokeWidth={3}
              dot={{ r: 5, stroke: "#811C50", fill: "#fff" }}
              activeDot={{ r: 7 }}
              name="Orders"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* cod , prepaid */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white shadow rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            COD vs Prepaid Orders
          </h2>

          <div className="flex justify-around items-center">
            <div className="flex flex-col items-center">
              <div className="relative w-24 h-24">
                <svg className="w-full h-full">
                  <circle
                    className="text-gray-200"
                    strokeWidth="10"
                    stroke="currentColor"
                    fill="transparent"
                    r="40"
                    cx="50%"
                    cy="50%"
                  />
                  <circle
                    className="text-blue-500"
                    strokeWidth="10"
                    strokeDasharray={`${
                      (data?.codOrdersCount / (data?.totalOrdersCount )) *
                      251
                    }, 251`}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r="40"
                    cx="50%"
                    cy="50%"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-blue-600">
                  {(
                    (data?.codOrdersCount / (data?.totalOrdersCount)) *
                    100
                  ).toFixed(0)}
                  %
                </span>
              </div>
              <p className="mt-2 text-sm font-medium text-gray-600">COD</p>
              <p className="text-lg font-bold text-blue-600">
                {data?.codOrdersCount}
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="relative w-24 h-24">
                <svg className="w-full h-full">
                  <circle
                    className="text-gray-200"
                    strokeWidth="10"
                    stroke="currentColor"
                    fill="transparent"
                    r="40"
                    cx="50%"
                    cy="50%"
                  />
                  <circle
                    className="text-green-500"
                    strokeWidth="10"
                    strokeDasharray={`${
                      (data?.prepaidOrdersCount /
                        (data?.totalOrdersCount )) *
                      251
                    }, 251`}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r="40"
                    cx="50%"
                    cy="50%"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-green-600">
                  {(
                    (data?.prepaidOrdersCount / (data?.totalOrdersCount )) *
                    100
                  ).toFixed(0)}
                  %
                </span>
              </div>
              <p className="mt-2 text-sm font-medium text-gray-600">Prepaid</p>
              <p className="text-lg font-bold text-green-600">
                {data?.prepaidOrdersCount}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-gray-700 ">
            Courier Wise Counts
          </h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={groupedCourierCounts}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                label
              >
                {groupedCourierCounts.map((entry, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tab Counts Bar Chart */}
      <div className="bg-white shadow rounded-2xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Orders by Status
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={tabCountsData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#E08FB1" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>

        {/* status + count list below */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-sm">
          {tabCountsData.map((item, i) => (
            <div
              key={i}
              className="flex justify-between border-b pb-1 text-gray-600"
            >
              <span className="font-medium">{item.name}</span>
              <span className="font-bold text-gray-800">{item.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
